// Vendor
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

// Bernie
import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ILocalization } from 'bernie-l10n';

// Stores
import { MultiItemStore } from 'stores/multi-item-store';
import { CurrentHotelStore } from 'stores/current-hotel-store';
import { MultiItemStoreStatus } from 'stores/multi-item-store-util';
import { SearchCriteriaModel } from 'src/common/stores/models/search-criteria-model';
import { SearchResultsStore } from 'stores/search-results-store';

// Models
import { BasicError, InCartProperty } from 'models/multi-item/multi-item-types';

// UITK
import { UitkPlaceholderCard, UitkPlaceholderCardPhase } from 'uitk-react-cards';
import { UitkDialogAnimationType, UitkDialogContent, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkErrorSummary } from 'uitk-react-error-summary';
import { UitkPageLoader } from 'uitk-react-loader';
import { UitkToolbar } from 'uitk-react-toolbar';

// Utils
import { generateAddAnotherPropertyURL } from './utils';

// Components
import { HoldingAreaActions } from 'components/multi-item/holding-area-actions';
import { InCartListing } from 'components/multi-item/holding-area-card';

export interface HoldingAreaContentProps {
  analytics?: AnalyticsStore;
  cartItemDeleter: (id) => () => void;
  closeDialog: () => void;
  getCartItems: () => Promise<InCartProperty[]>;
  goToCheckout: () => void;
  isAllSoldOut: () => boolean;
  l10n: ILocalization;
  currentHotel?: CurrentHotelStore;
  multiItem?: MultiItemStore;
  routeStore?: RouteStore;
  searchCriteria: SearchCriteriaModel;
  searchResults?: SearchResultsStore;
  error?: BasicError;
  selectTitleText: (itemCount: number) => string;
}

export interface HoldingAreaContentState {
  isNotFullCart: boolean;
}

@inject('analytics', 'currentHotel', 'multiItem', 'searchResults', 'routeStore')
@observer
export class HoldingAreaContent extends React.Component<HoldingAreaContentProps, HoldingAreaContentState> {
  public constructor(props) {
    super(props);

    this.addAnotherUrl = this.addAnotherUrl.bind(this);
    this.trackAddAnotherClick = this.trackAddAnotherClick.bind(this);

    this.state = {
      isNotFullCart: true,
    };
  }

  private addAnotherUrl(): object {
    const { searchCriteria, searchResults } = this.props;

    return generateAddAnotherPropertyURL(searchCriteria, searchResults);
  }

  private scrollAndRoute(routeStore: RouteStore, roomIndex: number, businessModelFilter: string) {
    const routeQuery = routeStore.mergeQueryString(
      routeStore.search,
      { roomIndex, businessModelFilter },
      key => !key.startsWith('pwaDialog')
    );

    const queryCombinerFunction = (prevQuery, newQuery) => newQuery;

    const scrollQuery = routeStore.mergeQueryString(
      routeStore.search,
      { scrollPosition: 'searchForm' },
      key => !key.startsWith('pwaDialog')
    );

    routeStore.updateSearch(scrollQuery, undefined, true, queryCombinerFunction);
    routeStore.rewriteHistory({ search: routeQuery });
  }

  private addMixAndMatchUrl(): void {
    const {
      multiItem: { itemCount, cartInfo },
      routeStore,
    } = this.props;
    const roomIndex = itemCount;

    const businessModelFilter = cartInfo && cartInfo.type;

    this.scrollAndRoute(routeStore, roomIndex, businessModelFilter);
  }

  private trackAddAnotherClick(rfrr): void {
    const { analytics } = this.props;
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(rfrr, 'Add Another Property', null, null, true);
    }
  }

  public componentDidUpdate(_prevProps, prevState) {
    const { currentHotel, multiItem } = this.props;
    const isNotFullCart =
      multiItem.showMixMatchExperience &&
      currentHotel &&
      currentHotel.searchCriteria &&
      currentHotel.searchCriteria.rooms &&
      multiItem.itemCount < currentHotel.searchCriteria.rooms.length;

    if (prevState.isNotFullCart !== isNotFullCart) {
      this.setState({
        isNotFullCart,
      });
    }
  }

  public render() {
    const {
      analytics,
      cartItemDeleter,
      closeDialog,
      getCartItems,
      goToCheckout,
      isAllSoldOut,
      l10n,
      multiItem,
      routeStore,
      currentHotel,
      selectTitleText,
    } = this.props;

    const { items, status, error } = multiItem;
    const formattedDate = date => l10n.formatDateString(date, { skeleton: 'MMMEd' });
    const processing = [MultiItemStoreStatus.FETCHING, MultiItemStoreStatus.PREPARE_CHECKOUT_PROCESSING].includes(status);
    const titleText = l10n.formatText(selectTitleText(multiItem.itemCount), multiItem.itemCount);

    return (
      <UitkFullscreenDialog
        openCallback={getCartItems}
        dialogShow={true}
        animation={UitkDialogAnimationType.FADE}
        returnFocusOnClose={true}
      >
        <UitkToolbar
          action={closeDialog}
          analytics={analytics.setupTracker('MultiProp', 'Collapse Holding Area')}
          header={titleText}
          iconLabel={l10n.formatText('hotels.infosite.paymentOptions.cancelButton.a11yLabel')}
        />
        <UitkDialogContent className="all-b-padding-twelve" zeroPadding={true}>
          <div className="all-b-padding-twelve">
            <UitkPageLoader visible={processing} />
            {error && error.message && (
              <div className="all-x-padding-three all-t-padding-three">
                <UitkErrorSummary heading={error.message} link={undefined} linkClickFocus={{}} linkClickFocusId="" />
              </div>
            )}
            {multiItem.showMixMatchExperience && items && items[0] && (
              <div className="all-x-padding-three all-t-padding-three">
                <p>{`${items[0].city}: ${formattedDate(items[0].checkin)} - ${formattedDate(items[0].checkout)}`}</p>
                <h3 className="uitk-cell uitk-type-400 pwa-theme--grey-900 truncate" data-stid="content-hotel-title">
                  {items[0].name}
                </h3>
              </div>
            )}
            <ul className="uitk-list">
              {items &&
                items.map(
                  (item, index) =>
                    item &&
                    item.id && (
                      <li key={item.id} className="all-x-padding-three all-t-padding-three">
                        {!multiItem.showMixMatchExperience && (
                          <h3 className="all-b-padding-two">
                            {`${item.city}: ${formattedDate(item.checkin)} - ${formattedDate(item.checkout)}`}
                          </h3>
                        )}
                        <InCartListing
                          {...item}
                          cartItemDeleter={cartItemDeleter}
                          isFirstItem={index === 0}
                          removeText={
                            multiItem.showMixMatchExperience
                              ? l10n.formatText('multiProperty.playback.mixAndMatch.changeRoom')
                              : l10n.formatText('remove')
                          }
                          analytics={analytics}
                          itemIndex={index}
                          l10n={l10n}
                          multiItem={multiItem}
                          routeStore={routeStore}
                          useRewards={currentHotel && currentHotel.searchCriteria && currentHotel.searchCriteria.useRewards}
                          scrollAndRoute={this.scrollAndRoute}
                        />
                      </li>
                    )
                )}
              <li className="all-x-padding-three all-t-padding-three">
                {!multiItem.showMixMatchExperience && (
                  <UitkPlaceholderCard
                    action={{
                      icon: 'add',
                      text: l10n.formatText('multiProperty.playback.addAnotherMessage'),
                      tag: Link,
                      // @ts-ignore
                      to: this.addAnotherUrl(),
                      onClick: () => this.trackAddAnotherClick('MultiProp.AddProperty'),
                    }}
                    startPhase={UitkPlaceholderCardPhase.NONE}
                  />
                )}
                {multiItem.showMixMatchExperience && this.state.isNotFullCart && (
                  <UitkPlaceholderCard
                    action={{
                      icon: 'add',
                      text: l10n.formatText('multiProperty.playback.mixAndMatch.selectNextRoom'),
                      onClick: () => {
                        this.addMixAndMatchUrl();
                        this.trackAddAnotherClick('MixMatch.AddProperty');
                      },
                    }}
                    startPhase={UitkPlaceholderCardPhase.NONE}
                  />
                )}
              </li>
            </ul>
          </div>
        </UitkDialogContent>
        <HoldingAreaActions goToCheckout={goToCheckout} l10n={l10n} isAllSoldOut={isAllSoldOut} isNotFullCart={this.state.isNotFullCart} />
      </UitkFullscreenDialog>
    );
  }
}
